import {AxiosInstance, AxiosResponse} from 'axios';
import {PlannedHoursParams} from './types';

export interface IShiftsApi {
    getMany<T>(dateFrom: string, dateTo: string, employees: Array<number>, projects: Array<number>, types: Array<number>, signal?: AbortSignal): Promise<AxiosResponse<T>>;
    get<T>(id: number): Promise<AxiosResponse<T>>;
    create<T>(body: T): Promise<AxiosResponse<T>>;
    update<T>(id: number, body: T): Promise<AxiosResponse<T>>;
    updateApproved<T>(id: number, value: boolean): Promise<AxiosResponse<T>>;
    updateBillable<T>(id: number, value: boolean): Promise<AxiosResponse<T>>;
    delete(id: number): Promise<AxiosResponse>;
    batchApprove(ids: Array<number>, value: boolean): Promise<AxiosResponse>
    batchBillable(ids: Array<number>, value: boolean): Promise<AxiosResponse>
    fetchPlannedHours(params: PlannedHoursParams): Promise<number>
}

export default class ShiftsApi implements IShiftsApi {
    private readonly _axiosInstance: AxiosInstance;
    private readonly _controller: string = 'shifts';
    private readonly _headers = { headers: { 'Content-Type': 'application/json' } };

    constructor(axiosInstance: AxiosInstance) {
        this._axiosInstance = axiosInstance;
    }

    async getMany<T>(dateFrom: string, dateTo: string, employees: number[], projects: number[], types: number[], signal?: AbortSignal): Promise<AxiosResponse<T>> {
        const employeesSearch = employees.map((x) => `&employees=${x}`).join('');
        const projectsSearch = projects.map((x) => `&projects=${x}`).join('');
        const typesSearch = types.map((x) => `&shiftTypes=${x}`).join('');
        return await this._axiosInstance.get<T>(`${this._controller}?from=${dateFrom}&to=${dateTo}${employeesSearch}${projectsSearch}${typesSearch}`, { signal });
    }
    async get<T>(id: number): Promise<AxiosResponse<T>> {
        return await this._axiosInstance.get<T>(`${this._controller}/${id}`);
    }
    async create<T>(body: T): Promise<AxiosResponse<T>> {
        return await this._axiosInstance.post<T>(`${this._controller}`, JSON.stringify(body), this._headers);
    }
    async update<T>(id: number, body: T): Promise<AxiosResponse<T>> {
        return await this._axiosInstance.put<T>(`${this._controller}/${id}`, JSON.stringify(body), this._headers);
    }
    async updateApproved<T>(id: number, value: boolean): Promise<AxiosResponse<T>> {
        return await this._axiosInstance.put<T>(`${this._controller}/${id}/approved`, JSON.stringify(value), this._headers);
    }
    async updateBillable<T>(id: number, value: boolean): Promise<AxiosResponse<T>> {
        return await this._axiosInstance.put<T>(`${this._controller}/${id}/billable`, JSON.stringify(value), this._headers);
    }
    async delete(id: number): Promise<AxiosResponse> {
        return await this._axiosInstance.delete(`${this._controller}/${id}`);
    }
    async batchApprove(ids: Array<number>, value: boolean): Promise<AxiosResponse> {
        const scope = ids.map(id => `&id=${id}`).join('');
        return await this._axiosInstance.put(`${this._controller}/approved-batch?${scope}`, value, this._headers);
    }
    async batchBillable(ids: Array<number>, value: boolean): Promise<AxiosResponse> {
        const scope = ids.map(id => `&id=${id}`).join('');
        return await this._axiosInstance.put(`${this._controller}/billable-batch?${scope}`, value, this._headers);
    }
    async fetchPlannedHours(params: PlannedHoursParams): Promise<number> {
        const { employeeId, startDate, endDate } = params;
        const url = `${this._controller}/planned-hours?employeeId=${employeeId}&startDate=${encodeURIComponent(
            startDate
        )}&endDate=${encodeURIComponent(endDate)}`;

        const res = await this._axiosInstance.get(url);

        if (!res) {
            throw new Error('Failed to fetch planned hours');
        }
        const data = await res.data.plannedHours;
        const planned = typeof data === 'number' ? data : data?.plannedHours;
        return Number(planned ?? 0);
    }
}
